<script>
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    captureHistorySnapshot,
    registerHistoryFlush,
} from "@app/store/historyStore.svelte.js";
import { autoReplaceState } from "@app/store/autoReplaceStore.svelte.js";
import { untrack } from "svelte";
import Quill from "quill";
import { clickoutside } from "@svelte-put/clickoutside";
import debounce from "just-debounce-it";
import quillClasses from "@cclib/dict/quillClasses.js";
import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import splitIntoParagraphs from "@cclib/functions/quill/splitIntoParagraphs.js";
import textLengthLimiter from "@cclib/functions/quill/textLengthLimiter.js";
import extractTextFromHtml from "@cclib/functions/quill/extractTextFromHtml.js";
import lineLength from "@cclib/functions/quill/lineLength.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import colorLookup from "@cclib/dict/colors.js";
import * as m from "@app/paraglide/messages.js";
import { environment } from "@app/store/envStore.svelte.js";
import {
    KEYBOARD_SHORTCUTS_UPDATED_EVENT,
    getStoredKeyboardShortcuts,
} from "@app/lib/keyboardShortcuts.js";
import {
    applyExcessCharacterHighlight,
    clearExcessCharacterHighlight,
} from "@app/lib/highlightExcessCharacters.js";
import { pauseWhileTyping } from "@app/lib/pauseWhileTyping.js";

let { event, eventIndex, textProperty = "text", autoFocus = true } = $props();
let isOriginalEditor = $derived(textProperty === "original");
let editorFontFamily = $derived(
    isOriginalEditor
        ? eventGroupState[projectState.selected]?.originalFontFamily || "arial"
        : eventGroupState[projectState.selected]?.fontFamily || "plexMono",
);
let editorAriaLabel = $derived(
    isOriginalEditor
        ? m.event_original_text_editor_aria_label({ eventIndex })
        : m.event_text_editor_aria_label({ eventIndex }),
);
// The first enabled list wins when shortforms collide.
let shortformLookup = $derived.by(() => {
    const lookup = new Map();
    autoReplaceState.forEach((list) => {
        if (!list.enabled) {
            return;
        }

        Object.entries(list.replacements).forEach(([shortform, replacement]) => {
            if (!lookup.has(shortform)) {
                lookup.set(shortform, {
                    replacement,
                    uppercase: list.uppercase,
                });
            }
        });
    });
    return lookup;
});
let paragraphs;
let quill;
let initialSelectionTimer;
let cursorRestoreTimer;
let disableEditingTimer;
let insertEventTimer;
let currentText = "";
let lastSyncedText = "";
let lastSyncedAlignment;
let lastSyncedRtl = false;
let quillDirty = false;
// A user edit is waiting on the debounce and has not reached history yet.
let historyPending = false;
let replacingShortform = false;
let customBindingKeys = [];

const Delta = Quill.import("delta");

// Typing pauses closer together than this collapse into one undo step.
const TYPING_COALESCE_MS = 1000;

function buildQuillShortcutBindings(shortcutGroups) {
    let shortcutBindings = {
        tab: {
            key: "Tab",
            handler: function () {
                return false;
            },
        },
    };

    shortcutGroups.forEach((shortcutGroup) => {
        shortcutGroup.shortcuts.forEach((shortcut) => {
            if (
                shortcut.keyCmd &&
                !shortcut.keyCmd.includes("+") &&
                shortcut.keyCmd !== "ArrowDown" &&
                shortcut.keyCmd !== "ArrowUp" &&
                shortcut.keyCmd !== "ArrowLeft" &&
                shortcut.keyCmd !== "ArrowRight"
            ) {
                shortcutBindings[shortcut.keyCmd] = {
                    key: shortcut.keyCmd,
                    handler: function () {
                        return false;
                    },
                };
            }
        });
    });

    return shortcutBindings;
}

let bindings = buildQuillShortcutBindings(
    getStoredKeyboardShortcuts(environment.mac),
);

function refreshExcessHighlight() {
    if (!quill?.root) {
        return;
    }

    applyExcessCharacterHighlight(
        quill.root,
        eventGroupState[projectState.selected]?.maxChars,
    );
}

function applyBindingsToQuill(quillInstance, shortcutGroups) {
    if (!quillInstance?.keyboard) {
        return;
    }

    customBindingKeys.forEach((key) => {
        delete quillInstance.keyboard.bindings[key];
    });
    customBindingKeys = [];

    const updatedBindings = buildQuillShortcutBindings(shortcutGroups);

    Object.entries(updatedBindings).forEach(([name, binding]) => {
        if (name === "tab") {
            return;
        }

        quillInstance.keyboard.bindings[name] = binding;
        customBindingKeys.push(name);
    });
}

function handleKeyboardShortcutsUpdated(e) {
    const shortcutGroups =
        e.detail?.shortcuts || getStoredKeyboardShortcuts(environment.mac);

    bindings = buildQuillShortcutBindings(shortcutGroups);
    applyBindingsToQuill(quill, shortcutGroups);
}

function setupQuillEditor(node) {
    return untrack(() => {
    let destroyed = false;
    quill = new Quill(node, {
        theme: "bubble",
        modules: {
            keyboard: {
                bindings: bindings,
            },
            toolbar: [
                ["bold", "underline", "italic"],
                [
                    {
                        color: colorLookup.colors,
                    },
                    {
                        background: colorLookup.colors,
                    },
                ],
                ["clean"],
            ],
            history: {
                maxStack: 0,
                userOnly: true,
            },
        },
        formats: [
            "bold",
            "underline",
            "italic",
            "align",
            "direction",
            "color",
            "background",
        ],
    });

    if (isSelectedEventGroupLocked()) {
        quill.enable(false);
    }

    if (!isOriginalEditor) {
        window.quillEditor = quill;
    }
    customBindingKeys = Object.keys(bindings).filter((key) => key !== "tab");

    window.addEventListener(
        KEYBOARD_SHORTCUTS_UPDATED_EVENT,
        handleKeyboardShortcutsUpdated,
    );

    applyEventToQuill();

    try {
        if (autoFocus && editorState.editing) {
            initialSelectionTimer = setTimeout(() => {
                if (destroyed) {
                    return;
                }

                quill.setSelection(
                    quill.getLength(),
                    0,
                    "silent",
                );
                editorState.editing = true;
            }, 50);
        } else {
            quill.blur();
        }
    } catch (err) {
        console.log("Error setting initial selection: ", err.message);
    }

    const toolbarContainer = quill.getModule("toolbar").container;
    const toolbarMouseDownHandler = (e) => {
        e.preventDefault();
    };
    toolbarContainer.addEventListener("mousedown", toolbarMouseDownHandler);

    const debouncedTextChange = debounce(
        (delta, oldDelta, source) => {
            if (destroyed) {
                return;
            }
            historyPending = false;

            if (
                !isOriginalEditor &&
                editorState.autoInsertEvent &&
                delta.ops[1] &&
                delta.ops[1].insert === "\n" &&
                source === "user"
            ) {
                paragraphs = splitIntoParagraphs(currentText);
                if (
                    paragraphs.length >
                    eventGroupState[projectState.selected].maxLines
                ) {
                    var range = quill.getSelection();
                    var length = quill.getLength();
                    event.text = paragraphs
                        .splice(
                            0,
                            eventGroupState[projectState.selected].maxLines,
                        )
                        .join("");

                    insertEvent({
                        text: paragraphs
                            .filter((p) => {
                                return !p.includes("<br>");
                            })
                            .join(""),
                        alignment: event.alignment,
                        xPos: event.xPos,
                        yPos: event.yPos,
                        style: event.style,
                        track: event.track ?? false,
                        start: editorState.insertStartOnInsert
                            ? player.currentTime
                            : false,
                        end: editorState.insertStartOnInsert
                            ? player.currentTime +
                              eventGroupState[projectState.selected].minDuration
                            : false,
                    });

                    let matches = paragraphs[0].match(/<p[^>]*>(.*?)<\/p>/);
                    if (
                        matches &&
                        matches[1] !== "<br>" &&
                        range?.index !== length - 2
                    ) {
                        eventGroupState[projectState.selected].selected = [
                            eventIndex,
                        ];

                        let delta = quill.clipboard.convert({
                            html: event.text,
                        });
                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();
                    }

                    saveToHistory();
                }
            } else if (
                editorState.autoLineBreak &&
                delta.ops[1] &&
                delta.ops[1].insert !== " " &&
                delta.ops[1].insert !== "\n" &&
                source === "user"
            ) {
                paragraphs = extractTextFromHtml(currentText);

                let lineErrors = lineLength
                    .getLineLengths(currentText)
                    .findIndex(
                        (len) =>
                            len > eventGroupState[projectState.selected].maxChars,
                    );

                if (lineErrors > -1) {
                    let cursorRange = quill.getSelection();
                    paragraphs = textLengthLimiter(
                        paragraphs,
                        eventGroupState[projectState.selected].maxChars,
                    );

                    if (!editorState.autoInsertEvent) {
                        let delta = quill.clipboard.convert({
                            html: convertToHtml(paragraphs.join("\n"), [
                                quillClasses.align[event.alignment],
                            ]),
                        });

                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();

                        cursorRestoreTimer = setTimeout(() => {
                            if (destroyed) {
                                return;
                            }

                            quill.setSelection(
                                cursorRange
                                    ? cursorRange.index
                                    : quill.getLength(),
                                0,
                                "silent",
                            );
                        }, 0);
                    } else if (editorState.autoInsertEvent) {
                        let delta = quill.clipboard.convert({
                            html: convertToHtml(
                                paragraphs
                                    .splice(
                                        0,
                                        eventGroupState[projectState.selected]
                                            .maxLines,
                                    )
                                    .join("\n"),
                                [quillClasses.align[event.alignment]],
                            ),
                        });
                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();

                        if (paragraphs.length > 0) {
                            insertEventTimer = setTimeout(() => {
                                if (destroyed) {
                                    return;
                                }

                                insertEvent({
                                    text: convertToHtml(paragraphs.join("\n"), [
                                        quillClasses.align[event.alignment],
                                    ]),
                                    alignment: event.alignment,
                                    xPos: event.xPos,
                                    yPos: event.yPos,
                                    style: event.style,
                                    vertical: event.vertical,
                                    track: event.track ?? false,
                                    start: editorState.insertStartOnInsert
                                        ? player.currentTime
                                        : false,
                                    end: editorState.insertStartOnInsert
                                        ? player.currentTime +
                                          eventGroupState[projectState.selected]
                                              .minDuration
                                        : false,
                                });
                            }, 0);
                        }
                    }

                    saveToHistory();
                } else {
                    setBoundText(currentText);
                    saveToHistory();
                }
            } else {
                setBoundText(currentText);

                if (source === "user") {
                    saveToHistory();
                }
            }
        },
        250,
        false,
    );

    const textChangeHandler = (delta, oldDelta, source) => {
        if (source === "silent") {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            return;
        }

        quillDirty = true;
        if (source === "user") {
            historyPending = true;
            // Runs per keystroke (not in the debounced handler) so playback
            // pauses as typing starts and resumes once it stops.
            pauseWhileTyping();

            if (!replacingShortform) {
                const separatorIndex = getSeparatorInsertIndex(delta);
                if (separatorIndex !== -1) {
                    replacingShortform = true;
                    try {
                        replaceShortformBeforeSeparator(separatorIndex);
                    } finally {
                        replacingShortform = false;
                    }
                }
            }
        }
        syncEventText();
        refreshExcessHighlight();

        debouncedTextChange(delta, oldDelta, source);
    };

    // Undo/Redo must see in-flight typing first, otherwise the debounced
    // capture lands after the undo and records a bogus step.
    const unregisterHistoryFlush = registerHistoryFlush(() => {
        if (!destroyed) {
            debouncedTextChange.flush();
        }
    });

    const selectionChangeHandler = function (range, oldRange, source) {
        if (source == "user" && range) {
            editorState.editing = true;
        }
    };

    quill.on("text-change", textChangeHandler);
    quill.on("selection-change", selectionChangeHandler);

    return () => {
        destroyed = true;
        unregisterHistoryFlush();
        debouncedTextChange.cancel();
        if (quill?.root) {
            clearExcessCharacterHighlight(quill.root);
        }
        if (quillDirty) {
            syncEventText({ guardExternalUpdates: true });
        }
        if (historyPending) {
            // The editor closed inside the debounce window; keep the edit undoable.
            historyPending = false;
            recordTextHistory();
        }
        clearTimeout(initialSelectionTimer);
        clearTimeout(cursorRestoreTimer);
        clearTimeout(disableEditingTimer);
        clearTimeout(insertEventTimer);
        quill.off("text-change", textChangeHandler);
        quill.off("selection-change", selectionChangeHandler);
        window.removeEventListener(
            KEYBOARD_SHORTCUTS_UPDATED_EVENT,
            handleKeyboardShortcutsUpdated,
        );

        if (toolbarContainer) {
            toolbarContainer.removeEventListener(
                "mousedown",
                toolbarMouseDownHandler,
            );
        }

        if (!isOriginalEditor && window.quillEditor === quill) {
            window.quillEditor = undefined;
        }
    };
    });
}

function getBoundText() {
    return event?.[textProperty] || "";
}

function setBoundText(value) {
    if (!event) {
        return;
    }

    event[textProperty] = value;
}

function getEventRtl() {
    const group = eventGroupState[projectState.selected];
    if (isOriginalEditor) {
        return !!group?.originalRtl;
    }
    return !!group?.rtl;
}

function applyEventToQuill({ preserveSelection = false } = {}) {
    if (!quill || !event) {
        return;
    }

    const range = preserveSelection ? quill.getSelection() : undefined;
    const eventText = getBoundText();
    const rtl = getEventRtl();
    const delta = quill.clipboard.convert({ html: eventText });

    quill.setContents(delta, "silent");
    quill.formatLine(
        0,
        99999,
        "align",
        event.alignment === "left" ? "" : event.alignment,
        "silent",
    );
    quill.formatLine(
        0,
        99999,
        "direction",
        rtl ? "rtl" : false,
        "silent",
    );

    currentText = quill.root.innerHTML;
    lastSyncedText = eventText;
    lastSyncedAlignment = event.alignment;
    lastSyncedRtl = rtl;
    quillDirty = false;

    if (range) {
        const length = quill.getLength();
        const index = Math.min(range.index, length - 1);

        quill.setSelection(
            index,
            Math.min(range.length, length - index - 1),
            "silent",
        );
    }

    refreshExcessHighlight();
}

$effect(() => {
    const locked = isSelectedEventGroupLocked();
    quill?.enable(!locked);
});

$effect(() => {
    const eventText = getBoundText();
    const alignment = event?.alignment;
    const rtl = getEventRtl();

    if (!quill || !event) {
        return;
    }

    if (
        eventText !== lastSyncedText ||
        alignment !== lastSyncedAlignment ||
        rtl !== lastSyncedRtl
    ) {
        const range = quill.getSelection();
        const delta = quill.clipboard.convert({ html: eventText });

        quill.setContents(delta, "silent");
        quill.formatLine(
            0,
            99999,
            "align",
            alignment === "left" ? "" : alignment,
            "silent",
        );
        quill.formatLine(
            0,
            99999,
            "direction",
            rtl ? "rtl" : false,
            "silent",
        );

        currentText = quill.root.innerHTML;
        lastSyncedText = eventText;
        lastSyncedAlignment = alignment;
        lastSyncedRtl = rtl;
        quillDirty = false;

        if (range) {
            const length = quill.getLength();
            const index = Math.min(range.index, length - 1);

            quill.setSelection(
                index,
                Math.min(range.length, length - index - 1),
                "silent",
            );
        }
    }

    refreshExcessHighlight();
});

$effect(() => {
    const maxChars = eventGroupState[projectState.selected]?.maxChars;
    if (!quill?.root) {
        return;
    }

    applyExcessCharacterHighlight(quill.root, maxChars);
});

function syncEventText({ guardExternalUpdates = false } = {}) {
    if (!quill || !event) {
        return;
    }

    if (isSelectedEventGroupLocked()) {
        return;
    }

    if (guardExternalUpdates && getBoundText() !== lastSyncedText) {
        quillDirty = false;
        return;
    }

    currentText = quill.root.innerHTML;
    setBoundText(currentText);
    lastSyncedText = currentText;
    lastSyncedAlignment = event.alignment;
    lastSyncedRtl = getEventRtl();
    quillDirty = false;
}

async function insertEvent(options) {
    try {
        if (isSelectedEventGroupLocked()) {
            return;
        }

        if (!eventGroupState[projectState.selected]) {
            return;
        }

        if (editorState.insertEndOnInsert) {
            event.end = player.currentTime;
        }
        eventGroupState[projectState.selected] = insertEv(
            eventGroupState[projectState.selected],
            options,
            eventIndex + 1,
        );
        eventGroupState[projectState.selected].selected = [eventIndex + 1];

        const selectedIndex = eventIndex + 1;

        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: selectedIndex },
            }),
        );

        captureHistorySnapshot({
            name: "insert event",
            eventGroup: projectState.selected,
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

const saveToHistory = function () {
    editorState.editing = true;
    recordTextHistory();
};

function recordTextHistory() {
    captureHistorySnapshot({
        name: "update text",
        eventGroup: projectState.selected,
        coalesceMs: TYPING_COALESCE_MS,
        coalesceKey: `${textProperty}:${event?.id ?? eventIndex}`,
    });
}

function capitalizeFirstLetter(string) {
    return string[0].toUpperCase() + string.slice(1);
}

// Returns the index of a single typed space/newline, or -1 for any other edit.
function getSeparatorInsertIndex(delta) {
    let index = 0;
    let separatorIndex = -1;

    for (const op of delta.ops) {
        if (op.retain !== undefined && !op.attributes) {
            index += op.retain;
        } else if (
            separatorIndex === -1 &&
            (op.insert === " " || op.insert === "\n")
        ) {
            separatorIndex = index;
            index += 1;
        } else {
            return -1;
        }
    }

    return separatorIndex;
}

function replaceShortformBeforeSeparator(separatorIndex) {
    if (separatorIndex <= 0 || shortformLookup.size === 0) {
        return false;
    }

    const text = quill.getText(0, separatorIndex);
    let start = separatorIndex;
    while (start > 0 && !/\s/.test(text[start - 1])) {
        start--;
    }

    const shortform = text.slice(start);
    const match = shortform && shortformLookup.get(shortform);
    if (!match || !match.replacement) {
        return false;
    }

    const replacement =
        match.uppercase && start === 0
            ? capitalizeFirstLetter(match.replacement)
            : match.replacement;
    const formats = quill.getContents(start, shortform.length).ops[0]
        ?.attributes;
    const hadSelection = !!quill.getSelection();

    quill.updateContents(
        new Delta()
            .retain(start)
            .delete(shortform.length)
            .insert(replacement, formats),
        "user",
    );

    if (hadSelection) {
        // Quill's Enter binding sets its own selection after this text-change,
        // so the cursor has to be placed once that handler returns.
        const editor = quill;
        const cursorIndex = start + replacement.length + 1;
        queueMicrotask(() => {
            if (editor.root.isConnected) {
                editor.setSelection(cursorIndex, 0, "silent");
            }
        });
    }

    return true;
}

function isInsideQuillEditor(node) {
    if (!(node instanceof Element)) {
        return false;
    }

    return !!node.closest(".QuillEditor");
}

function disableEditing(e) {
    syncEventText();

    const clickTarget = e?.detail?.target;
    if (
        isInsideQuillEditor(clickTarget) ||
        isInsideQuillEditor(document.activeElement)
    ) {
        return;
    }

    let inputs = ['input', 'select', 'button', 'textarea'];
    let activeElementTag = document.activeElement.tagName.toLowerCase();
    if (!inputs.includes(activeElementTag)) {
        document.body.focus();
    }

    disableEditingTimer = setTimeout(() => {
        editorState.editing = false;        
    }, 50);
}

function selectEditor() {
    if (quill && !quill.getSelection()) {
        quill.setSelection(
            quill.getLength(),
            0,
            "silent",
        );
    }
}
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
    id="QuillEditor-{textProperty}-{eventIndex}"
    class="QuillEditor rounded"
    role="region"
    aria-label={editorAriaLabel}
    style="font-family: '{editorFontFamily}' !important; line-height: 101%; font-size: {eventGroupState[
        projectState.selected
    ]?.fontSize || 18}px;"
    {@attach setupQuillEditor}
    use:clickoutside
    onclickoutside={disableEditing}
    onclick={() => selectEditor()}>
</div>

<style lang="css">
.QuillEditor {
    background-color: #000;
    color: #fff;
    height: auto;
    min-height: 160px;
    word-break: break-word;
}
</style>
